<script setup lang="ts">
  import { Typo, TypoVariant } from '@travelata-libs/ui-kit';
  import { computed, ref } from 'vue';
  import { Field, FieldSize } from '@/entities/searchForm';
  import { useOutsidePointer } from '@/shared/composables';
  import { type NightsRange } from '@/shared/model';
  import NightsRangeDropdown from './NightsRangeDropdown.vue';
  import { NIGHTS_RANGE_TEXTS } from '../config/texts';
  import { normalizeRange } from '../lib/nightsRange';
  import { useNightsRange } from '../model/useNightsRange';

  interface NightsRangeFieldProps {
    modelValue?: NightsRange | null;
    checkInLabel?: string;
    summaryPrefix?: string;
    size?: `${FieldSize}`;
    loading?: boolean;
    disabled?: boolean;
  }

  const props = withDefaults(defineProps<NightsRangeFieldProps>(), {
    modelValue: null,
    checkInLabel: '',
    summaryPrefix: undefined,
    size: FieldSize.M,
    loading: false,
    disabled: false,
  });

  interface NightsRangeFieldEmits {
    (e: 'update:modelValue', range: NightsRange): void;
    (e: 'manual'): void;
  }

  const emit = defineEmits<NightsRangeFieldEmits>();

  const model = computed<NightsRange>({
    get: () => normalizeRange(props.modelValue),
    set: (value) => emit('update:modelValue', value),
  });

  const core = useNightsRange({
    model,
    checkInLabel: () => props.checkInLabel,
    summaryPrefix: () => props.summaryPrefix,
  });

  const rootRef = ref<HTMLElement | null>(null);

  const onToggle = (): void => {
    core.toggle();
  };

  useOutsidePointer(rootRef, { active: core.controlActivated, handler: core.close });

  const onUpdateFrom = (data: number) => {
    core.setFromValue(data);
    emit('manual');
  };

  const onUpdateTo = (data: number) => {
    core.setToValue(data);
    emit('manual');
  };

  defineExpose({
    validate: (): boolean => core.validate(),
  });
</script>

<script lang="ts">
  export default {
    model: {
      prop: 'modelValue',
      event: 'update:modelValue',
    },
  };
</script>

<template>
  <div
    ref="rootRef"
    class="nights-range-field"
    data-testid="nights-range-field"
  >
    <NightsRangeDropdown
      :from="core.from.value"
      :to="core.to.value"
      :visible="core.controlActivated.value"
      :summary="core.summaryLabel.value"
      @update:from="onUpdateFrom"
      @update:to="onUpdateTo"
      @close="core.close"
    >
      <template #reference>
        <Field
          :label="NIGHTS_RANGE_TEXTS.fieldLabel"
          :active="core.controlActivated.value"
          :loading="loading"
          :disabled="disabled"
          :size="size"
          value-testid="nights-range-value"
          @click="onToggle"
        >
          <template #value>
            <ClientOnly>
              <Typo
                :variant="TypoVariant.BodyLRegular"
                tag="span"
              >
                {{ core.valueLabel.value }}
              </Typo>
            </ClientOnly>
          </template>
        </Field>
      </template>
    </NightsRangeDropdown>
  </div>
</template>

<style scoped lang="scss">
  .nights-range-field {
    position: relative;
    width: 100%;
  }
</style>
